import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { CustomVideoOrderSummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

export function ModelCustomVideosCard(): ReactElement {
  const { api } = useAuth();
  const [orders, setOrders] = useState<CustomVideoOrderSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quoteCoins, setQuoteCoins] = useState<Record<string, string>>({});

  const load = useCallback(async (): Promise<void> => {
    const result = await api.listMyCustomVideoOrders();
    setOrders(result.orders);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.customVideos.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function respond(
    orderId: string,
    action: 'quote' | 'accept' | 'decline',
  ): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      const quotedCoins =
        action === 'quote' ? Number.parseInt(quoteCoins[orderId] ?? '', 10) : undefined;
      if (action === 'quote' && (!Number.isFinite(quotedCoins) || (quotedCoins ?? 0) < 10)) {
        setError(t('common.studio.customVideos.invalidQuote'));
        return;
      }
      await api.respondCustomVideoOrder(orderId, {
        action,
        ...(quotedCoins !== undefined ? { quotedCoins } : {}),
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.customVideos.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.customVideos.loading')}</p>
    );
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.customVideos.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.customVideos.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      {orders.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.customVideos.empty')}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {orders.map((order) => (
            <li
              key={order.id}
              style={{
                padding: '0.75rem 0',
                borderBottom: '1px solid var(--fm-color-border)',
              }}
            >
              <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem' }}>
                @{order.fanUsername ?? 'fan'} · {order.status} · {order.offeredCoins} coins
              </p>
              <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>{order.brief}</p>
              {order.status === 'REQUESTED' || order.status === 'QUOTED' ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input
                    type="number"
                    min={10}
                    placeholder={t('common.studio.customVideos.quotePlaceholder')}
                    value={quoteCoins[order.id] ?? ''}
                    onChange={(event) =>
                      setQuoteCoins((prev) => ({ ...prev, [order.id]: event.target.value }))
                    }
                    style={{ width: '7rem' }}
                  />
                  <Button
                    label={t('common.studio.customVideos.quote')}
                    disabled={isSaving}
                    onClick={() => {
                      void respond(order.id, 'quote');
                    }}
                  />
                  <Button
                    label={t('common.studio.customVideos.accept')}
                    disabled={isSaving}
                    onClick={() => {
                      void respond(order.id, 'accept');
                    }}
                  />
                  <Button
                    label={t('common.studio.customVideos.decline')}
                    variant="ghost"
                    disabled={isSaving}
                    onClick={() => {
                      void respond(order.id, 'decline');
                    }}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
